import { z } from 'zod';
import { isAfter, isBefore } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import { parseUTCDate } from 'utils/dateTimeUtils';

/**
 * Form Data Structure (Input):
 * {
 *   createSlackChannel?: 'true' | 'false';  // String values for ActionButtonGroupField
 *   slackChannelName?: string;  // For new channels
 *   slackChannelId?: string;  // For existing channels
 *   slackTeamId?: string;  // For workspace selection (when feature flag is on)
 *   slackChannelDate?: Date;  // Date in form - local time
 *   slackChannelAutoarchive?: boolean;  // Auto-archive setting
 * }
 *
 * Database Data Structure (Output after transform):
 * {
 *   createSlackChannel?: boolean;  // Transformed from string to boolean
 *   slackChannelName?: string;  // For new channels
 *   slackChannelId?: string;  // For existing channels
 *   slackTeamId?: string;  // For workspace selection (when feature flag is on)
 *   slackChannelDate?: string;  // Date in UTC format for DB
 *   slackChannelAutoarchive?: boolean;  // Auto-archive setting
 * }
 */

export const convertSlackChannelDateToFormValue = (
  slackChannelDate: string,
  eventTimezone?: string
): Date => {
  const utcDate = parseUTCDate(slackChannelDate);
  const eventTz = eventTimezone ?? 'UTC';
  const wallClockTime = formatInTimeZone(utcDate, eventTz, 'yyyy-MM-dd HH:mm:ss');
  const result = new Date(wallClockTime.replace(' ', 'T'));
  return result;
};

export const convertSlackChannelDateToDBValue = (
  slackChannelDate: Date,
  eventTimezone?: string
): string => {
  const eventTz = eventTimezone ?? 'UTC';
  const utcDate = zonedTimeToUtc(slackChannelDate, eventTz);
  return utcDate.toISOString();
};

export const getSlackSettingsSchema = (
  isSlackRevampEnabled: boolean,
  datetimeStart?: string,
  eventTimezone?: string,
  eventSlackChannelDate?: string
): z.ZodType<any> =>
  z
    .object({
      createSlackChannel: z
        .enum(['true', 'false'])
        .transform((value: string): boolean => value === 'true'),
      slackChannelName: z
        .string()
        .min(1, 'Channel name is required')
        .transform((value) => value.trim()),
      slackChannelId: z.string().nullable().optional(),
      slackTeamId: z
        .string()
        .nullable()
        .optional()
        .refine(
          (slackTeamId) => {
            return !isSlackRevampEnabled || slackTeamId;
          },
          {
            message: 'Please select a workspace first.'
          }
        ),
      slackChannelDate: z
        .date()
        .transform((date: Date): string | undefined =>
          date ? convertSlackChannelDateToDBValue(date, eventTimezone) : undefined
        )
        .optional()
        .refine(
          (slackChannelDate) => {
            if (
              !slackChannelDate ||
              (eventSlackChannelDate &&
                new Date(eventSlackChannelDate).getTime() === new Date(slackChannelDate).getTime())
            )
              return true;

            return isAfter(parseUTCDate(slackChannelDate), new Date());
          },
          {
            message: 'Creation date must be in the future'
          }
        )
        .refine(
          (slackChannelDate) => {
            if (!slackChannelDate || !datetimeStart) return true;
            return isBefore(parseUTCDate(slackChannelDate), parseUTCDate(datetimeStart));
          },
          {
            message: 'Creation date must be before the event starts'
          }
        ),
      slackChannelAutoarchive: z.boolean().nullable().optional()
    })
    .superRefine((data, ctx) => {
      if (data.createSlackChannel) {
        // Slack create rules, verified against the live API: lowercase letters
        // and decimal digits in any script, combining marks, hyphens, underscores.
        // Uppercase, emoji, and ①-style numbers are rejected or normalized away.
        // Existing channels: Slack already validated.
        if (!/^[\p{Ll}\p{Lo}\p{Nd}\p{M}_-]+$/u.test(data.slackChannelName)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Only lowercase letters, numbers, hyphens, underscores. To use a different name, create the channel in Slack and connect it here instead.',
            path: ['slackChannelName']
          });
        }
        // Slack's 80-"character" limit is enforced as 80 UTF-8 bytes (verified
        // live): longer names are byte-truncated, then created under a silently
        // shortened name or rejected when the cut splits a multibyte character.
        if (new TextEncoder().encode(data.slackChannelName).length > 80) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Channel name must be 80 characters or fewer (less for non-Latin scripts)',
            path: ['slackChannelName']
          });
        }
        if (data.slackChannelDate === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Creation date is required',
            path: ['slackChannelDate']
          });
        }
      } else if (!data.slackChannelId || data.slackChannelId.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a channel',
          path: ['slackChannelId']
        });
      }
    });

export type SlackSettingsFormInput = z.input<ReturnType<typeof getSlackSettingsSchema>>; // Form values
export type SlackSettingsFormValues = z.infer<ReturnType<typeof getSlackSettingsSchema>>; // DB values

/**
 * Convert database values to form input values
 * Used when loading initial values from the database
 */
export const convertDBToFormData = (
  dbData: SlackSettingsFormValues,
  eventTimezone?: string
): SlackSettingsFormInput => {
  let createSlackChannel: 'true' | 'false' | undefined;
  if (dbData.createSlackChannel === undefined || !dbData.slackChannelName) {
    createSlackChannel = undefined;
  } else {
    createSlackChannel = dbData.createSlackChannel ? 'true' : 'false';
  }

  // Test both functions when converting from DB to form
  let slackChannelDateResult: Date | undefined;
  if (dbData.slackChannelDate) {
    slackChannelDateResult = convertSlackChannelDateToFormValue(
      dbData.slackChannelDate,
      eventTimezone
    );
  }

  return {
    createSlackChannel,
    slackChannelName: dbData.slackChannelName || undefined,
    slackChannelId: dbData.slackChannelId || undefined,
    slackTeamId: dbData.slackTeamId || undefined,
    slackChannelDate: slackChannelDateResult,
    slackChannelAutoarchive: dbData.slackChannelAutoarchive || false
  };
};
